import { Checkbox } from "@/src/components/ui/checkbox";
import { useToast } from "@/src/components/ui/use-toast";
import { Card } from "@/src/components/wrappers/Card";
import { formatDateUTC } from "@/src/helpers/formatDateUTC";
import { useBookingAcceptCardPaymentsMutation } from "@/src/lib/bookingAcceptCardPaymentsMutation";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { PaymentEmailDialog } from "../../PaymentEmailDialog";

const PaymentSchedulesSection = ({
  paymentScheduleCustomerData,
  paymentScheduleOperatorData,
  currencyCode,
  operatorCurrencyCode,
  bookingId,
  initialAcceptCardPayments,
}: {
  paymentScheduleCustomerData: any;
  paymentScheduleOperatorData: any;
  currencyCode: string;
  operatorCurrencyCode: string;
  bookingId: string;
  initialAcceptCardPayments?: boolean;
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [acceptCardPayments, setAcceptCardPayments] = useState(initialAcceptCardPayments || false);
  // Manual payment reminder for an unpaid instalment — same editable email module as receipts.
  const [remindStep, setRemindStep] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const updateAcceptCardPayments = useBookingAcceptCardPaymentsMutation(bookingId);
  const { toast } = useToast();

  const formatPrice = (price: number, toCurrency: string) => {
    const formattedNumber = new Intl.NumberFormat("en-EN", { style: "currency", currency: toCurrency }).format(price);

    return `${formattedNumber}`;
  };
  const paymentStatus = (amount: number, paidSoFar: number) => {
    if (paidSoFar >= amount) {
      return "Paid";
    } else if (paidSoFar > 0 && paidSoFar < amount) {
      return "Partially paid";
    }
    return "Not paid";
  };

  const getPaymentLabel = (index: number) => {
    const labels = ["First payment", "Second payment", "Third payment", "Fourth payment"];
    return labels[index] || `Payment ${index + 1}`;
  };

  const sortedCustomerPayments = [...(paymentScheduleCustomerData?.data || [])].sort(
    (a, b) => a.scheduleStep - b.scheduleStep,
  );

  const sortedOperatorPayments = [...(paymentScheduleOperatorData?.data || [])].sort(
    (a, b) => a.scheduleStep - b.scheduleStep,
  );

  const isPaymentToday = (date: string) => {
    const today = new Date();
    const dateToCompare = new Date(date);
    return (
      today.getDate() === dateToCompare.getDate() &&
      today.getMonth() === dateToCompare.getMonth() &&
      today.getFullYear() === dateToCompare.getFullYear()
    );
  };

  const submitAcceptcardPayments = () => {
    const toastId = toast({
      description: "Updating good to know section...",
      variant: "default",
      duration: Infinity,
    }).id;
    updateAcceptCardPayments.mutate(
      { acceptCardPayments: !acceptCardPayments, toastId },
      {
        onSuccess: () => {
          setAcceptCardPayments((prevState) => !prevState);
        },
      },
    );
  };

  return (
    <Card className="w-full flex flex-col gap-y-6">
      <div className="flex flex-col gap-y-4">
        <div className="flex items-center">
          <h2 className="text-xl font-semibold">Payment schedules</h2>
          <Link
            to={`/bookings/${bookingId}/edit-payment-schedule?tab=payments`}
            className="ml-auto text-blue-600 font-semibold"
          >
            Edit
          </Link>
        </div>
        <div className="flex items-center gap-x-2">
          <Checkbox className="w-6 h-6" checked={acceptCardPayments} onClick={() => submitAcceptcardPayments()} />
          <p>Accept card payments</p>
        </div>

        {/* Customer's Payments */}
        <div className="flex flex-col gap-y-2">
          <h4 className="text-base font-medium">Customer's</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-2 gap-y-2 md:gap-y-0">
            {sortedCustomerPayments?.map((item: any, index: number) => {
              const status = paymentStatus(+item?.amount, +item?.paidSoFar);
              const remainingAmount = +item?.amount - +item?.paidSoFar;

              return (
                <div
                  key={index}
                  className="flex gap-y-2 bg-slate-50 rounded-lg p-4 border-blue-100 border-solid border"
                >
                  <div>
                    <p className="text-xs font-medium mb-1 text-slate-700">{getPaymentLabel(index)}</p>
                    <p className="text-sm font-medium">
                      {status === "Partially paid" ? (
                        <div className="flex flex-col justify-start">
                          <p className="flex items-center mr-auto text-xs font-normal pr-2 rounded-3xl">
                            {formatPrice(+item?.paidSoFar, currencyCode)} paid
                          </p>
                          <div>
                            <span className="line-through text-base">{formatPrice(item?.amount, currencyCode)}</span>{" "}
                            <span className="text-base">{formatPrice(remainingAmount, currencyCode)}</span>
                          </div>
                        </div>
                      ) : (
                        <span className="text-base">{formatPrice(item?.amount, currencyCode)}</span>
                      )}
                    </p>
                    <div className="flex items-center gap-1">
                      <p className="text-sm font-regular text-slate-700 mt-1">
                        Due {formatDateUTC(item?.dueDate, "UTC", undefined, undefined, true, undefined, true)}{" "}
                      </p>
                      {isPaymentToday(item?.dueDate) && (
                        <span className="flex items-center text-xs font-medium ml-auto pt-1 pb-1 pl-2 pr-2 mt-1 bg-orange-100 rounded-3xl h-7">
                          Today
                        </span>
                      )}
                    </div>
                  </div>
                  {status !== "Not paid" && (
                    <p className="flex items-center text-xs font-medium ml-auto pt-1 pb-1 pl-2 pr-2 bg-blue-100 rounded-3xl h-7">
                      {status}
                    </p>
                  )}
                  {status === "Not paid" && item.reminderSent && (
                    <p className="flex flex-col items-center text-xs font-medium mb-auto ml-auto pt-1 pb-1 pl-4 pr-4 bg-yellow-100 rounded-3xl text-slate-900">
                      Reminder sent
                      {item.reminderSentAt && <span>{formatDateUTC(item.reminderSentAt, "UTC")}</span>}
                    </p>
                  )}
                  {status !== "Paid" && item?.id && (
                    <button type="button" title="Email a payment reminder for this instalment to the customer (or to yourself)" onClick={() => setRemindStep(String(item.id))} className={`flex items-center text-xs font-medium mb-auto pt-1 pb-1 pl-3 pr-3 rounded-3xl h-7 text-blue-700 hover:bg-blue-50 ${status === "Not paid" && item.reminderSent ? "ml-2" : "ml-auto"}`}>Remind</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {remindStep && (
          <PaymentEmailDialog bookingId={bookingId} paymentId={remindStep} kind="reminder" onClose={() => setRemindStep(null)} onSent={() => { queryClient.invalidateQueries({ queryKey: ["paymentScheduleCustomerData", bookingId] }); queryClient.invalidateQueries({ queryKey: ["paymentEmails", bookingId] }); }} />
        )}

        {/* HELM Payments */}
        <div className="flex flex-col gap-y-2">
          <h4 className="text-base font-medium">HELM</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-2 gap-y-2 md:gap-y-0">
            {sortedOperatorPayments?.map((item: any, index: number) => {
              const status = paymentStatus(+item?.amount, +item?.paidSoFar);
              const remainingAmount = +item?.amount - +item?.paidSoFar;

              return (
                <div
                  key={index}
                  className="flex gap-y-2 bg-slate-50 rounded-lg p-4 border-blue-100 border-solid border"
                >
                  <div>
                    <p className="text-xs font-medium mb-1 text-slate-700">{getPaymentLabel(index)}</p>
                    <p className="text-sm font-medium">
                      {status === "Partially paid" ? (
                        <div className="flex flex-col justify-start">
                          <p className="flex items-center mr-auto text-xs font-normal pr-2 rounded-3xl">
                            {formatPrice(+item?.paidSoFar, operatorCurrencyCode)} paid
                          </p>
                          <div>
                            <span className="line-through text-base">{formatPrice(item?.amount, operatorCurrencyCode)}</span>{" "}
                            <span className="text-base">{formatPrice(remainingAmount, operatorCurrencyCode)}</span>
                          </div>
                        </div>
                      ) : (
                        <span className="text-base">{formatPrice(item?.amount, operatorCurrencyCode)}</span>
                      )}
                    </p>
                    <p className="text-sm font-regular text-slate-700 mt-1">
                      Due {formatDateUTC(item?.dueDate, "UTC", undefined, undefined, true)}
                    </p>
                  </div>
                  {status !== "Not paid" && (
                    <p className="flex items-center text-xs font-medium ml-auto pt-1 pb-1 pl-2 pr-2 bg-blue-100 rounded-3xl h-7">
                      {status}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default PaymentSchedulesSection;
